import { Chip, Stack } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { useState } from 'react'

import { CarrierType, CarrierVisitStatus } from '@yard/app/api'
import { CarrierTypeAutoComplete } from '@yard/components/AutoCompletes/CarrierTypeAutoComplete/CarrierTypeAutoComplete'
import { CarrierVisitStatusAutoComplete } from '@yard/components/AutoCompletes/CarrierVisitStatusAutoComplete/CarrierVisitStatusAutoComplete'
import { DateRangeAutoComplete } from '@yard/components/AutoCompletes/DateRangeAutoComplete/DateRangeAutoComplete'
import { DateRangeEnum } from '@yard/constants/dateRanges'
import { CarrierVisitAllocationRulesViewStore } from '@yard/pages/YardSetup/stores/allocation-rules/carrier-visit-allocation-rules.view-store'
import { GlobalAllocationRulesViewStore } from '@yard/pages/YardSetup/stores/allocation-rules/global-allocation-rules.view-store'
import { CarrierVisitOverviewViewStore } from '@yard/pages/YardSetup/stores/carrier-visit-overview.viewstore'
import { YardViewStore } from '@yard/pages/YardSetup/stores/yard.view-store'
import { observer } from 'mobx-react-lite'

type Props = {
  yardViewStore: YardViewStore
  carrierVisitOverviewViewStore: CarrierVisitOverviewViewStore
  carrierVisitAllocationRulesViewStore: CarrierVisitAllocationRulesViewStore
  globalAllocationRulesViewStore: GlobalAllocationRulesViewStore
}

export const CarrierVisitOverviewFilters = observer((props: Props) => {
  const theme = useMinimalsTheme()
  const { t } = useTranslate()
  const { carrierVisitOverviewViewStore } = props

  const [selectedTypes, setSelectedTypes] = useState<CarrierType[]>([])
  const [selectedStatuses, setSelectedStatuses] = useState<CarrierVisitStatus[]>([])

  const handleClearTypes = () => {
    setSelectedTypes([])
    carrierVisitOverviewViewStore.setSelectedTypes([])
  }

  const handleClearStatuses = () => {
    setSelectedStatuses([])
    carrierVisitOverviewViewStore.setSelectedStatus([])
  }

  const autocompleteStyle = {
    margin: 0,
    '& .MuiOutlinedInput-root': {
      padding: '6px 28px 6px 2px !important',
      borderRadius: '6px',
      backgroundColor: '#ffffff',
      paddingRight: '8px',
      fontSize: '12px',
      fontWeight: 600,
    },
    '& fieldset': {
      borderColor: `${theme.palette.grey[300]} !important`,
    },
    '& .MuiOutlinedInput-input': {
      padding: '8px 12px',
    },
  }

  const getChipAutocompleteStyle = (hasValue: boolean) => ({
    ...autocompleteStyle,
    '& .MuiOutlinedInput-root': {
      ...autocompleteStyle['& .MuiOutlinedInput-root'],
      ...(hasValue && { flexWrap: 'nowrap' }),
    },
    '& .MuiOutlinedInput-input': {
      ...autocompleteStyle['& .MuiOutlinedInput-input'],
      ...(hasValue && {
        width: 0,
        minWidth: '0 !important',
        padding: '0 !important',
      }),
    },
  })

  return (
    <Stack
      display='flex'
      direction='row'
      alignItems='center'
      p={theme.customSpacing.s}
      spacing={theme.customSpacing.xxs}
      bgcolor={theme.palette.grey[200]}
      borderBottom={`1px solid ${theme.palette.grey[300]}`}
    >
      <Stack flex={4}>
        <DateRangeAutoComplete
          size='small'
          label=''
          defaultValue={DateRangeEnum.ThirtyDays}
          displayPopupIcon
          disableClearable
          sx={autocompleteStyle}
          onChange={(val: DateRangeEnum) =>
            carrierVisitOverviewViewStore.setSelectedDateFilter(val)
          }
        />
      </Stack>
      <Stack flex={4}>
        <CarrierTypeAutoComplete
          size='small'
          label=''
          multiple
          displayPopupIcon
          disableClearable
          sx={getChipAutocompleteStyle(selectedTypes.length > 0)}
          value={selectedTypes}
          onChange={val => {
            setSelectedTypes(val)
            carrierVisitOverviewViewStore.setSelectedTypes(val)
          }}
          renderTags={value =>
            value.length > 0 ? (
              <Chip
                label={`${t('carrier', 'Carrier')} (${value.length})`}
                size='small'
                sx={{ fontSize: '12px', borderRadius: '4px', paddingRight: 0, paddingLeft: 0 }}
                onDelete={handleClearTypes}
              />
            ) : null
          }
        />
      </Stack>
      <Stack flex={4}>
        <CarrierVisitStatusAutoComplete
          size='small'
          label=''
          multiple
          displayPopupIcon
          disableClearable
          sx={getChipAutocompleteStyle(selectedStatuses.length > 0)}
          value={selectedStatuses}
          onChange={val => {
            setSelectedStatuses(val)
            carrierVisitOverviewViewStore.setSelectedStatus(val)
          }}
          renderTags={value =>
            value.length > 0 ? (
              <Chip
                label={`${t('status', 'Status')} (${value.length})`}
                size='small'
                sx={{ fontSize: '12px', borderRadius: '4px', paddingRight: 0, paddingLeft: 0 }}
                onDelete={handleClearStatuses}
              />
            ) : null
          }
        />
      </Stack>
    </Stack>
  )
})
